import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import SEOHead from '@/components/SEOHead';
import { ThemeProvider } from '@/components/ThemeProvider';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Search, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Search page component
 * Handles search queries from Google's structured data
 * Provides search functionality for the website content
 */
const Search = () => {
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const navigate = useNavigate();

  // Get search query from URL parameters
  const query = searchParams.get('q') || searchParams.get('search_term_string') || '';

  useEffect(() => {
    if (query) {
      setSearchQuery(query);
      performSearch(query);
    }
  }, [query]);

  // Mock search functionality - in a real implementation, this would search through actual content
  const performSearch = async (searchTerm: string) => {
    setIsSearching(true);
    
    // Simulate search delay
    setTimeout(() => {
      // Mock search results based on the search term
      const mockResults = [
        {
          title: "Web Development Services",
          description: "Professional web development services including React, TypeScript, and modern web technologies.",
          url: "/#services",
          type: "Service"
        },
        {
          title: "Trading Bot Project",
          description: "Advanced trading bot that analyses historical price data and generates trading signals.",
          url: "/projects/trading-bot",
          type: "Project"
        },
        {
          title: "Matthew's Clothing Store",
          description: "E-commerce website with responsive design and modern user interface.",
          url: "/projects/clothing-store",
          type: "Project"
        },
        {
          title: "Contact Information",
          description: "Get in touch with Matthew Innes for web development and digital design services.",
          url: "/#contact",
          type: "Contact"
        }
      ].filter(result => 
        result.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        result.description.toLowerCase().includes(searchTerm.toLowerCase())
      );

      setSearchResults(mockResults);
      setIsSearching(false);
    }, 500);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      performSearch(searchQuery.trim());
    }
  };

  return (
    <>
      <SEOHead 
        title={`Search Results for "${query}" - Matthew Innes Portfolio`}
        description={`Search results for "${query}" on Matthew Innes portfolio website. Find web development projects, services, and contact information.`}
        canonical={`https://matthewweb.com/search?q=${encodeURIComponent(query)}`}
      />
      <ThemeProvider>
        <div className="min-h-screen flex flex-col bg-background text-foreground dark:bg-navy dark:text-lightSlate">
          <Navbar />
          <main className="flex-grow container mx-auto px-4 py-16 max-w-4xl">
            <div className="mb-8">
              <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center text-teal hover:text-teal/80 transition-colors mb-4"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Portfolio
              </button>
              
              <h1 className="text-3xl md:text-4xl font-bold text-foreground dark:text-lightestSlate mb-4">
                Search Results
              </h1>
              
              {query && (
                <p className="text-lg text-foreground/70 dark:text-lightSlate mb-8">
                  Results for: <span className="font-semibold text-teal">"{query}"</span>
                </p>
              )}
            </div>

            {/* Search form */}
            <form onSubmit={handleSearch} className="mb-8">
              <div className="flex gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-foreground/50 w-5 h-5" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search the portfolio..."
                    className="w-full pl-10 pr-4 py-3 bg-background/50 dark:bg-navy/60 border border-border dark:border-slate/30 rounded-lg focus:border-teal focus:outline-none text-foreground dark:text-lightSlate"
                  />
                </div>
                <button
                  type="submit"
                  className="px-6 py-3 bg-teal hover:bg-teal/90 text-navy font-medium rounded-lg transition-colors"
                >
                  Search
                </button>
              </div>
            </form>

            {/* Search results */}
            {isSearching ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal"></div>
                <p className="mt-4 text-foreground/70 dark:text-lightSlate">Searching...</p>
              </div>
            ) : searchResults.length > 0 ? (
              <div className="space-y-6">
                {searchResults.map((result, index) => (
                  <div key={index} className="bg-white dark:bg-navy/40 p-6 rounded-lg border border-border dark:border-slate/20">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-foreground dark:text-lightestSlate mb-2">
                          <a 
                            href={result.url}
                            className="hover:text-teal transition-colors"
                          >
                            {result.title}
                          </a>
                        </h3>
                        <p className="text-foreground/70 dark:text-lightSlate mb-3">
                          {result.description}
                        </p>
                        <span className="inline-block px-3 py-1 bg-teal/10 text-teal text-sm rounded-full">
                          {result.type}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : query ? (
              <div className="text-center py-12">
                <Search className="mx-auto w-16 h-16 text-foreground/30 dark:text-lightSlate/30 mb-4" />
                <h3 className="text-xl font-semibold text-foreground dark:text-lightestSlate mb-2">
                  No results found
                </h3>
                <p className="text-foreground/70 dark:text-lightSlate">
                  Try searching for "web development", "projects", or "contact"
                </p>
              </div>
            ) : (
              <div className="text-center py-12">
                <Search className="mx-auto w-16 h-16 text-foreground/30 dark:text-lightSlate/30 mb-4" />
                <h3 className="text-xl font-semibold text-foreground dark:text-lightestSlate mb-2">
                  Search the Portfolio
                </h3>
                <p className="text-foreground/70 dark:text-lightSlate">
                  Enter a search term above to find projects, services, and information
                </p>
              </div>
            )}
          </main>
          <Footer />
        </div>
      </ThemeProvider>
    </>
  );
};

export default Search;
